<template>
    <div>
        <div class="main-container">
            <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

            <!-- Back nav -->
            <div class="req-back">
                <a href="javascript:void(0)" @click="$router.push({ path: '/kids-device', query:{ profileId: this.profileId }})" class="req-back__link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Home
                </a>
            </div>

            <!-- Tabs -->
            <div class="req-tabs">
                <button
                    class="req-tab"
                    :class="{ 'req-tab--active': activeTab === 'All' }"
                    @click="activeTab = 'All'"
                >
                    <svg class="req-tab__icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.8"/>
                        <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.8"/>
                        <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.8"/>
                        <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.8"/>
                    </svg>
                    All
                    <span class="req-tab__badge" v-if="allCount > 0">{{ allCount }}</span>
                </button>
                <button
                    class="req-tab"
                    :class="{ 'req-tab--active': activeTab === 'Applications' }"
                    @click="activeTab = 'Applications'"
                >
                    <svg class="req-tab__icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="3" width="18" height="18" rx="4" stroke="currentColor" stroke-width="1.8"/>
                        <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                    </svg>
                    Applications
                    <span class="req-tab__badge" v-if="applicationsCount > 0">{{ applicationsCount }}</span>
                </button>
                <button
                    class="req-tab"
                    :class="{ 'req-tab--active': activeTab === 'Contacts' }"
                    @click="activeTab = 'Contacts'"
                >
                    <svg class="req-tab__icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="8" r="3.4" stroke="currentColor" stroke-width="1.8"/>
                        <path d="M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    </svg>
                    Contacts
                    <span class="req-tab__badge" v-if="contactsCount > 0">{{ contactsCount }}</span>
                </button>
            </div>

            <!-- Request Cards -->
            <div class="req-list">
                <!-- Empty state -->
                <div class="req-empty" v-if="filteredData.length === 0">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="#d1d5db" stroke-width="1.5"/>
                        <path d="M8 12h8" stroke="#d1d5db" stroke-width="1.5" stroke-linecap="round"/>
                    </svg>
                    <span>No pending requests</span>
                </div>

                <!-- Unified card loop — no v-if mixed with v-for -->
                <template v-for="item in filteredData" :key="item._id">

                    <!-- Contact Add / Remove -->
                    <div class="req-card" v-if="['Contact Add', 'Contact Remove'].includes(item.requestType)">
                        <div class="req-card__top">
                            <div class="req-card__contact-avatar">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.7"/>
                                    <path d="M4 20c0-3.1 3.6-6 8-6s8 2.9 8 6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
                                </svg>
                            </div>
                            <div class="req-card__info">
                                <div class="req-card__name-row">
                                    <span class="req-card__name">{{ item.name }}</span>
                                    <span class="req-type-badge" :class="getBadgeClass(item)">
                                        {{ item.requestType }}
                                    </span>
                                </div>
                                <span class="req-card__phone">{{ item.phone }}</span>
                                <span class="req-card__time">{{ new Date(item.created_at).toLocaleString() }}</span>
                            </div>
                        </div>
                        <div class="req-card__actions" v-if="item.status == 'Pending'">
                            <button class="req-btn req-btn--approve" @click="approveContact(item)">{{ getPrimaryLabel(item) }}</button>
                            <button class="req-btn req-btn--reject" @click="rejectRequest(item._id)">{{ getRejectLabel(item) }}</button>
                        </div>
                    </div>

                    <!-- App Install / Uninstall / Extend -->
                    <div class="req-card" v-else>
                        <div class="req-card__top">
                            <div class="req-card__icon">
                                <img
                                    v-if="item.playstoreDetail"
                                    :src="item.playstoreDetail.icon"
                                    width="44" height="44"
                                    style="border-radius: 12px; display: block;"
                                />
                                <div v-else class="req-card__icon-fallback">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <rect x="3" y="3" width="18" height="18" rx="4" stroke="currentColor" stroke-width="1.7"/>
                                    </svg>
                                </div>
                            </div>
                            <div class="req-card__info">
                                <div class="req-card__name-row">
                                    <span class="req-card__name">
                                        <a v-if="item.playstoreDetail"
                                            href="javascript:void(0)"
                                            @click="$router.push({ path: '/application', query: { package_name: item.packageName, profile_id: profileId, serialNumber: serialNumber } })"
                                            class="req-card__name-link">
                                            {{ item.playstoreDetail.title }}
                                        </a>
                                        <span v-else>{{ item.packageName }}</span>
                                    </span>
                                    <span class="req-type-badge" :class="getBadgeClass(item)">
                                        {{ item.requestType }}
                                    </span>
                                </div>
                                <span class="req-card__remark" v-if="item.remarks">{{ item.remarks }}</span>
                                <span class="req-card__extend-info" v-if="item.requestType === 'Extend Time'">
                                    +{{ item.extendedTime }} min requested
                                </span>
                                <div class="req-card__ratings" v-if="item.playstoreDetail">
                                    <span class="req-card__rating-item">
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="#f59e0b">
                                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                        </svg>
                                        {{ item?.playstoreDetail?.scoreText }}
                                    </span>
                                    <span class="req-card__rating-item">
                                        <img v-if="item.kidsRating.avgKidsRating >= 3" :src="childPositiveSmile" width="13" />
                                        <img v-else-if="item.kidsRating.avgKidsRating >= 2" :src="childNeutralSmile" width="13" />
                                        <img v-else-if="item.kidsRating.avgKidsRating >= 1" :src="childNegativeSmile" width="13" />
                                        <i v-else class="far fa-smile" style="font-size:13px; color:#9ca3af;"></i>
                                        {{ item.overallRating }}
                                    </span>
                                </div>
                                <span class="req-card__time">{{ new Date(item.created_at).toLocaleString() }}</span>
                            </div>
                        </div>
                        <div class="req-card__actions" v-if="item.status == 'Pending'">
                            <button
                                class="req-btn req-btn--approve"
                                @click="openDailyLimitSetPopup(item.playstoreDetail, item._id, item)"
                                :disabled="isDisabled"
                            >
                                <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i></span>
                                <span v-else>{{ getPrimaryLabel(item) }}</span>
                            </button>
                            <button class="req-btn req-btn--reject" @click="rejectRequest(item._id)">{{ getRejectLabel(item) }}</button>
                        </div>
                    </div>

                </template>
            </div>

            <!-- Daily Limit Popup -->
            <div class="collapse footer" id="dailyLimitSetPopup">
                <div class="req-popup">
                    <div class="req-popup__header">
                        <span class="req-popup__title">{{ app ? 'Install: ' + app.title : 'Set Daily Limit' }}</span>
                        <button class="req-popup__close" @click="closeDailyLimitSetPopup()">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </div>
                    <div class="req-popup__body">
                        <p class="req-popup__desc">Set a daily time limit to ensure healthy app usage. The app will be disabled once the limit is reached.</p>

                        <div class="req-popup__toggle-row">
                            <span class="req-popup__toggle-label">Enable daily limit</span>
                            <label class="kid-action-switch">
                                <input type="checkbox" v-model="enableDailyLimit" />
                                <span class="kid-action-switch__track"></span>
                            </label>
                        </div>

                        <div class="req-popup__limit-row">
                            <span class="req-popup__limit-label">Daily limit (minutes)</span>
                            <div class="req-popup__stepper">
                                <button class="req-popup__step-btn" @click="decreaseLimit()">
                                    <ArrowLeftIcon />
                                </button>
                                <input
                                    type="text"
                                    class="req-popup__step-input"
                                    :disabled="!enableDailyLimit"
                                    v-model="dailyLimitTime"
                                    @blur="roundOF15()"
                                />
                                <button class="req-popup__step-btn" @click="increaseLimit()">
                                    <ArrowRightIcon />
                                </button>
                            </div>
                        </div>

                        <button class="req-action-btn req-action-btn--primary" @click="saveAppDailyLimit()" :disabled="isDisabled">
                            <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i> Processing...</span>
                            <span v-else>Install with Time Limit</span>
                        </button>
                        <button class="req-action-btn req-action-btn--secondary" @click="saveAppDailyLimit(true)" :disabled="isDisabled">
                            <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i></span>
                            <span v-else>Install without Time Limit</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Extend Limit Popup -->
            <div class="collapse footer" id="extendDailyLimitSetPopup">
                <div class="req-popup">
                    <div class="req-popup__header">
                        <span class="req-popup__title">{{ app ? 'Extend: ' + app.title : 'Extend Limit' }}</span>

                        <button class="req-popup__close" @click="closeExtendDailyLimitSetPopup()">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </div>
                    <div class="req-popup__body">
                        <p class="req-popup__desc">Your child has requested additional time. Review and approve to extend their daily limit.</p>

                        <div class="req-popup__info-row">
                            <span>Current limit</span>
                            <span class="req-popup__info-val">{{ currentDailyLimit }} min</span>
                        </div>
                        <!-- Extend popup new total — guard against null -->
                        <div class="req-popup__info-row">
                            <span>New total</span>
                            <span class="req-popup__info-val req-popup__info-val--highlight">
                                {{ (currentDailyLimit || 0) + (dailyLimitTime || 0) }} min
                            </span>
                        </div>

                        <div class="req-popup__limit-row">
                            <span class="req-popup__limit-label">Extend by (minutes)</span>
                            <div class="req-popup__stepper">
                                <button class="req-popup__step-btn" @click="decreaseLimit()">
                                    <ArrowLeftIcon />
                                </button>
                                <input
                                    type="text"
                                    class="req-popup__step-input"
                                    :disabled="!enableDailyLimit"
                                    v-model="dailyLimitTime"
                                    @blur="roundOF15()"
                                />
                                <button class="req-popup__step-btn" @click="increaseLimit()">
                                    <ArrowRightIcon />
                                </button>
                            </div>
                        </div>

                        <div class="inline-alert inline-alert--warning" v-if="categoryId">
                            <div class="inline-alert__icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18C1.64 18.3 1.55 18.65 1.55 19C1.55 20.1 2.45 21 3.55 21H20.45C21.55 21 22.45 20.1 22.45 19C22.45 18.65 22.36 18.3 22.18 18L13.71 3.86C13.17 2.9 11.83 2.9 11.29 3.86H10.29Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                            <div class="inline-alert__body">
                                <span class="inline-alert__title">Category disabled</span>
                                <span class="inline-alert__msg">The application category is disabled. <a href="javascript:void(0)" @click="$router.push({ path: '/category', query: { id: categoryId, profileId: profileId, serialNumber: serialNumber, appRequestPage: true }})">Go to category</a> to enable it first.</span>
                            </div>
                        </div>

                        <button class="req-action-btn req-action-btn--primary" @click="approveExtendDailyLimitRequest()" :disabled="isDisabled">
                            <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i> Saving...</span>
                            <span v-else>Approve Extension</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Error Modal -->
            <div class="modal fade" id="errorModal" tabindex="-1" role="dialog" aria-hidden="true">
                <div class="modal-dialog modal-dialog-centered" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title text-danger">Error <i class="fas fa-exclamation-triangle"></i></h5>
                            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div class="modal-body">{{ error }}</div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-primary" @click="gotToCategory()">Go To Category</button>
                        </div>
                    </div>
                </div>
            </div>

            <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
        </div>
    </div>
</template>

<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'
import KidsService from "../../services/KidsService";
import ProfileService from "../../services/ProfileService";
import ArrowLeftIcon from '@/components/icons/ArrowLeftIcon.vue'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'

export default {
  name: 'App Requests',
  components: {
    KidHeader,
    KidFooter,
    ArrowLeftIcon,
    ArrowRightIcon
  },
  props: {
    notRated: { type: String, default: "./images/not-certified.png" },
    exportOrange: { type: String, default: "./images/experts-orange.png" },
    exportGreen: { type: String, default: "./images/experts-green.png" },
    sixteenPlus: { type: String, default: "./images/16-plus.png" },
    childPositiveSmile: { type: String, default: "./images/child-positive-smile.png" },
    childNeutralSmile: { type: String, default: "./images/child-neutral-smile.png" },
    childNegativeSmile: { type: String, default: "./images/child-negative-smile.png" },
  },
  data() {
    return {
        isDisabled: false,
        profileId: this.$route.query.profileId,
        serialNumber: this.$route.query.serialNumber,
        tableData: [],
        activeTab: 'All',
        enableDailyLimit: true,
        dailyLimitTime: 60,
        currentDailyLimit: 0,
        app: null,
        requestId: "",
        item: null,
        categoryId: null,
        error: "",
    }
  },
  computed: {
    filteredData() {
        if (this.activeTab === 'All') {
            return this.tableData;
        }
        if (this.activeTab === 'Applications') {
            return this.tableData.filter(i => ['App Install', 'App Uninstall', 'Extend Time'].includes(i.requestType));
        }
        if (this.activeTab === 'Contacts') {
            return this.tableData.filter(i => ['Contact Add', 'Contact Remove'].includes(i.requestType));
        }
        return [];
    },
    allCount() {
        return this.tableData.length;
    },
    applicationsCount() {
        return this.tableData.filter(i => ['App Install', 'App Uninstall', 'Extend Time'].includes(i.requestType)).length;
    },
    contactsCount() {
        return this.tableData.filter(i => ['Contact Add', 'Contact Remove'].includes(i.requestType)).length;
    },
  },
  methods: {
    // Primary (approve-side) button label per request type
    getPrimaryLabel(item) {
        switch (item.requestType) {
            case 'App Install': return 'Install';
            case 'App Uninstall': return 'Uninstall';
            case 'Extend Time': return 'Extend';
            case 'Contact Add': return 'Add';
            case 'Contact Remove': return 'Remove';
            default: return 'Approve';
        }
    },
    // Reject-side button label — Contact Remove requests use "Ignore" instead of "Reject"
    getRejectLabel(item) {
        return item.requestType === 'Contact Remove' ? 'Ignore' : 'Reject';
    },
    // Distinct badge color per request type (5 types, 5 colors)
    getBadgeClass(item) {
        switch (item.requestType) {
            case 'App Install': return 'req-type-badge--install';
            case 'App Uninstall': return 'req-type-badge--uninstall';
            case 'Extend Time': return 'req-type-badge--extend';
            case 'Contact Add': return 'req-type-badge--contact-add';
            case 'Contact Remove': return 'req-type-badge--contact-remove';
            default: return '';
        }
    },
    async getAppRequestList() {
        let data = { profileId: this.profileId, serialNumber: this.serialNumber, status: "Pending" };
        await KidsService.getAppRequestList(data)
        .then((response) => {
          this.tableData = response.data.data;
          //this.$toast.success(response.data.respones_msg);
        }).catch((e) => {
          this.$toast.error(e.response.data.respones_msg);
        });
    },
    async rejectRequest(id) {
        let data = { profileId: this.profileId, serialNumber: this.serialNumber, id: id };
        await KidsService.rejectRequest(data)
        .then((response) => {
            this.getAppRequestList();
          //this.$toast.success(response.data.respones_msg);
        }).catch((e) => {
          this.$toast.error(e.response.data.respones_msg);
        });
    },
    approveContact(item) {
        let data = { profileId: this.profileId, serialNumber: this.serialNumber, id: item._id };
        KidsService.approveContact(data)
        .then((response) => {
            this.applyPolicy();
            this.getAppRequestList();
            //this.$toast.success(response.data.respones_msg);
        }).catch((e) => {
            this.$toast.error(e.response.data.respones_msg);
        });
    },
    async approveRequest(app, requestId, item, skipDailyConfiguration = false) {
        if (!app) {
            return this.$toast.error("Playstore detail not found for this app, please try again after sometime.");
        }
        if (item.requestType == "App Uninstall") {
            this.isDisabled = true;
            let data = { package_name: item.packageName, profile_id: this.profileId, serialNumber: this.serialNumber, requestId: requestId };
            await ProfileService.uninstallProfileApplication(data)
                .then(async (response) => {
                    this.applyPolicy();
                    this.getAppRequestList();
                    this.deleteProfileApplication(item.packageName);
                    this.isDisabled = false;
                    //this.$toast.success(response.data.respones_msg);
                }).catch((e) => {
                    this.isDisabled = false;
                    this.$toast.error(e.response.data.respones_msg);
                });
        } else {
            this.isDisabled = true;
            let masterApplicationData = { product_id: app.appId, package_name: app.appId, app_icon: app.icon, application_details: app, type: "Android" };
            await ProfileService.createMasterApplication(masterApplicationData).then(() => {}).catch(() => {});
            let data = {
                profile_id: this.profileId, package_name: app.appId, is_web_app: false, type: "Android",
                serialNumber: this.serialNumber, requestId: requestId,
                enableDailyLimit: (skipDailyConfiguration == true) ? false : this.enableDailyLimit,
                dailyLimitTime: (skipDailyConfiguration == true) ? 0 : this.dailyLimitTime
            };
            await ProfileService.createProfileApplication(data)
            .then((response) => {
                this.applyPolicy();
                this.isDisabled = false;
                //this.$toast.success(response.data.respones_msg);
                this.enableDailyLimit = true;
                this.dailyLimitTime = 60;
                $('#dailyLimitSetPopup').collapse('hide');
                this.getAppRequestList();
            }).catch((e) => {
                this.isDisabled = false;
                this.$toast.error(e.response.data.respones_msg);
            });
        }
    },
    deleteProfileApplication(package_name) {
        this.isDisabled = true;
        var data = { package_name: package_name, profile_id: this.profileId, serialNumber: this.serialNumber };
        ProfileService.deleteProfileApplication(data)
        .then((response) => { this.isDisabled = false; 
            //this.$toast.success(response.data.respones_msg);
         })
        .catch((e) => { this.isDisabled = false; this.$toast.error(e.response.data.respones_msg); });
    },
    async applyPolicy() {
        this.isDisabled = true;
        let data = { profile_id: this.profileId, serialNumber: this.serialNumber };
        await ProfileService.applyPolicy(data)
        .then((response) => { this.isDisabled = false; 
            //this.$toast.success(response.data.respones_msg);
         })
        .catch(() => { this.isDisabled = false; });
    },
    async openDailyLimitSetPopup(app, requestId, item) {
        if (item.requestType == "App Uninstall") {
            return await this.approveRequest(app, requestId, item);
        }
        this.app = app;
        this.requestId = requestId;
        this.item = item;
        if (item.requestType == "Extend Time") {
            this.dailyLimitTime = item.extendedTime;
            this.currentDailyLimit = (item.profileAppDetail.dailyLimitTime) ? parseInt((item.profileAppDetail.dailyLimitTime)) : 0;
            $('#extendDailyLimitSetPopup').collapse('show');
        } else {
            $('#dailyLimitSetPopup').collapse('show');
        }
    },
    async approveExtendDailyLimitRequest() {
        let data = { id: this.item._id, extendLimit: this.dailyLimitTime };
        await KidsService.approveExtendDailyLimitRequest(data)
        .then((response) => {
            this.applyPolicy();
            this.categoryId = null;
            this.currentDailyLimit = 0;
            this.dailyLimitTime = 0;
            $('#extendDailyLimitSetPopup').collapse('hide');
            this.getAppRequestList();
            //this.$toast.success(response.data.respones_msg);
        }).catch((e) => {
            $('#extendDailyLimitSetPopup').collapse('hide');
            this.error = e.response.data.respones_msg;
            this.categoryId = e.response.data.data;
            $('#errorModal').modal('show');
        });
    },
    gotToCategory() {
        $('#errorModal').modal('hide');
        this.$router.push({ path: '/category', query: { id: this.categoryId, profileId: this.profileId, serialNumber: this.serialNumber, appRequestPage: true }});
    },
    async saveAppDailyLimit(skipDailyConfiguration) {
        return await this.approveRequest(this.app, this.requestId, this.item, skipDailyConfiguration);
    },
    closeDailyLimitSetPopup() { $('#dailyLimitSetPopup').collapse('hide'); },
    closeExtendDailyLimitSetPopup() { $('#extendDailyLimitSetPopup').collapse('hide'); },
    decreaseLimit() { if (this.dailyLimitTime > 0) { this.dailyLimitTime = this.dailyLimitTime - 15; } },
    increaseLimit() { this.dailyLimitTime = (parseInt(this.dailyLimitTime) + 15); },
    roundOF15() { this.dailyLimitTime = Math.ceil(this.dailyLimitTime / 15.0) * 15; },
  },
  watch: {
    enableDailyLimit() {
        this.dailyLimitTime = this.enableDailyLimit ? 15 : 0;
    },
    dailyLimitTime() {
        if (this.dailyLimitTime < 0) this.dailyLimitTime = 0;
    },
  },
  beforeMount() {
    this.getAppRequestList();
  },
}
</script>

<style scoped>
/* ── Back nav ── */
.req-back {
    padding: 12px 14px 10px;
    background: #fff;
    width: 100%;
    text-align: left;
}

.req-back__link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
}

.req-back__link:active { opacity: 0.7; }

/* ── Tabs ── */
.req-tabs {
    display: flex;
    gap: 8px;
    padding: 0 14px 14px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    background: #fff;
    border-bottom: 0.5px solid #e5e7eb;
    margin-bottom: 10px;
    width: 100%;
}

.req-tabs::-webkit-scrollbar { display: none; }

.req-tab {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 9px 16px;
    border-radius: 22px;
    border: 0.5px solid #e5e7eb;
    background: #fff;
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    cursor: pointer;
    white-space: nowrap;
    flex: 1;
    -webkit-tap-highlight-color: transparent;
    transition: all 0.15s;
}

.req-tab__icon {
    flex-shrink: 0;
    opacity: 0.75;
}

.req-tab--active .req-tab__icon { opacity: 1; }

.req-tab--active {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
}

.req-tab__badge {
    background: #f3f4f6;
    color: #2563eb;
    font-size: 10px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 10px;
    min-width: 16px;
    text-align: center;
}

.req-tab--active .req-tab__badge {
    background: rgba(255,255,255,0.25);
    color: #fff;
}

/* ── Request list ── */
.req-list {
    padding: 8px 14px 100px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
}

/* ── Empty state ── */
.req-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 40px 14px;
    font-size: 13px;
    color: #9ca3af;
}

/* ── Request Card ── */
.req-card {
    background: #fff;
    border-radius: 14px;
    border: 0.5px solid #e5e7eb;
    overflow: hidden;
    text-align: left;
    transition: box-shadow 0.15s;
}

.req-card__top {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 14px 10px;
}

.req-card__icon { flex-shrink: 0; }

.req-card__icon-fallback {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #f3f4f6;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
}

.req-card__contact-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #eff6ff;
    color: #2563eb;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.req-card__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.req-card__name-row {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.req-card__name {
    font-size: 13px;
    font-weight: 600;
    color: #111;
}

.req-card__name-link {
    color: #111;
    text-decoration: none;
}

.req-card__remark,
.req-card__phone {
    font-size: 12px;
    color: #6b7280;
}

.req-card__extend-info {
    font-size: 12px;
    font-weight: 600;
    color: #d97706;
    text-align: left;
}

.req-card__time {
    font-size: 11px;
    color: #9ca3af;
    text-align: left;
}

.req-card__ratings {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.req-card__rating-item {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    color: #6b7280;
}

/* ── Type badges ── */
.req-type-badge {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 20px;
    white-space: nowrap;
}

.req-type-badge--install { background: #dcfce7; color: #15803d; }
.req-type-badge--uninstall { background: #fee2e2; color: #dc2626; }
.req-type-badge--extend { background: #fef3c7; color: #92400e; }
.req-type-badge--contact-add { background: #dbeafe; color: #1d4ed8; }
.req-type-badge--contact-remove { background: #f3e8ff; color: #7e22ce; }

/* ── Action buttons ── */
.req-card__actions {
    display: flex;
    gap: 8px;
    padding: 0 14px 14px;
}

.req-btn {
    flex: 1;
    height: 38px;
    border-radius: 10px;
    border: none;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: opacity 0.15s;
}

.req-btn:active { opacity: 0.8; }
.req-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.req-btn--approve {
    background: #2563eb;
    color: #fff;
}

.req-btn--reject {
    background: #fee2e2;
    color: #dc2626;
}

/* ── Popup panels ── */
.req-popup {
    background: #fff;
    border-radius: 14px;
    border: 0.5px solid #e5e7eb;
    margin: 8px 0;
    overflow: hidden;
}

.req-popup__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px 12px;
    border-bottom: 0.5px solid #f3f4f6;
}

.req-popup__title {
    font-size: 14px;
    font-weight: 700;
    color: #111;
}

.req-popup__close {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #f3f4f6;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #374151;
}

.req-popup__body {
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.req-popup__desc {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.5;
    margin: 0;
}

.req-popup__toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f9fafb;
    border-radius: 10px;
    padding: 10px 12px;
}

.req-popup__toggle-label {
    font-size: 13px;
    font-weight: 500;
    color: #111;
}

.req-popup__limit-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.req-popup__limit-label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    flex: 1;
}

.req-popup__stepper {
    display: flex;
    align-items: center;
    border: 0.5px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
}

.req-popup__step-btn {
    width: 36px;
    height: 36px;
    background: #f9fafb;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #374151;
    -webkit-tap-highlight-color: transparent;
}

.req-popup__step-btn:active { background: #f3f4f6; }

.req-popup__step-input {
    width: 56px;
    height: 36px;
    border: none;
    border-left: 0.5px solid #e5e7eb;
    border-right: 0.5px solid #e5e7eb;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: #111;
    background: #fff;
    outline: none;
}

.req-popup__info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: #f9fafb;
    border-radius: 8px;
    font-size: 13px;
    color: #6b7280;
}

.req-popup__info-val {
    font-weight: 700;
    color: #111;
}

.req-popup__info-val--highlight { color: #2563eb; }

.req-action-btn {
    width: 100%;
    height: 44px;
    border-radius: 12px;
    border: none;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}

.req-action-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.req-action-btn:active { opacity: 0.85; }

.req-action-btn--primary { background: #2563eb; color: #fff; }
.req-action-btn--secondary { background: #f3f4f6; color: #374151; }

/* ── Reuse switch from dashboard ── */
.kid-action-switch {
    position: relative;
    display: inline-flex;
    width: 38px;
    height: 20px;
    cursor: pointer;
    margin: 0;
}

.kid-action-switch input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
}

.kid-action-switch__track {
    position: absolute;
    inset: 0;
    background: #d1d5db;
    border-radius: 999px;
    transition: background 0.2s;
}

.kid-action-switch__track::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 14px;
    height: 14px;
    background: white;
    border-radius: 50%;
    transition: transform 0.2s;
}

.kid-action-switch input:checked + .kid-action-switch__track { background: #2563eb; }
.kid-action-switch input:checked + .kid-action-switch__track::after { transform: translateX(18px); }

/* ── Reuse inline alert ── */
.inline-alert {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 10px 12px;
    border-radius: 12px;
    border-left: 3px solid transparent;
    text-align: left;
}

.inline-alert--warning { background: #fffbeb; border-left-color: #f59e0b; }
.inline-alert__icon { flex-shrink: 0; margin-top: 1px; color: #f59e0b; }
.inline-alert__body { display: flex; flex-direction: column; gap: 3px; flex: 1; }
.inline-alert__title { font-size: 12px; font-weight: 600; color: #92400e; }
.inline-alert__msg { font-size: 12px; color: #78350f; line-height: 1.4; }
.inline-alert__msg a { color: #b45309; font-weight: 600; text-decoration: underline; }

</style>